import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Button,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { VirtualProductQuoteRequest } from '../../../models/virtualProduct/types';

interface StepTwoProps {
  form: VirtualProductQuoteRequest;
  setForm: React.Dispatch<React.SetStateAction<VirtualProductQuoteRequest>>;
  onNext: () => void;
}

const brandKeyMap: Record<string, string> = {
  'Harvey Windows + Doors': 'harvey',
  'Andersen': 'andersen',
  'Simonton': 'simonton',
  'MI Windows & Doors': 'miwindows',
  'Other': 'default',
};

const brandWindowTypesMap: Record<string, string[]> = {
  harvey: [
    'Double Hung', 'Single Hung',
    'Casement', 'Awning',
    'Gliding', 'Picture',
    'Bay', 'Bow',
    'Garden', 'Rolling',
    'Hopper', 'Horizontal Slider'
  ],

  miwindows: [
    'Double Hung', 'Single Hung',
    'Casement', 'Awning',
    'Picture', 'Specialty',
    'Horizontal Slider'
  ],
  andersen: [
    'Double Hung', 'Awning', 'Bay',
    'Bow', 'Casement', 'Single Hung',
    'Gliding', 'Picture', 'Specialty'
  ],
  simonton: [
    'Double Hung', 'Casement',
    'Awning', 'Picture',
    'Specialty', 'Sliding',
    'Bay', 'Bow',
    'Garden', 'Combination'
  ],
  default: [
    'Double Hung', 'Single Hung',
    'Casement', 'Awning',
    'Gliding', 'Picture',
    'Specialty', 'Custom Shape',
    'Sliding', 'Bay',
    'Bow', 'Garden',
    'Rolling', 'Hopper',
    'Combination', 'Horizontal Slider',
    'Customer/Other'
  ]
};

const brandProductSeriesMap: Record<string, string[]> = {
  andersen: ['A-Series', 'E-Series', '400 Series', '200 Series', '100 Series', 'Aluminum'],
  miwindows: ['V2000 Series', 'V3000 Series'],
};

const brandProductLinesMap: Record<string, string[]> = {
  harvey: [
    'Slimline Single-Hung',
    'Slimline Double-Hung',
    'Tribute Double-Hung',
    'Classic Double-Hung',
    'Vinyl',
    'Majesty',
  ],
};

const brands = ['Harvey Windows + Doors', 'Andersen', 'Simonton', 'MI Windows & Doors', 'Other'];
const materials = ['Vinyl', 'Wood', 'Aluminum', 'Composite', 'Fiberglass'];
const windowCounts = ['', '1-5', '6-10', '11-20', '21+'];

const simontonWindowCollections = [
  '5500 Collection',
  '5050 Collection',
  'StormBreaker Plus Collection',
  'Asure Collection',
  '9800 Collection',
  '6500 Collection',
  '6100 Collection',
  'Premium Collection',
  'Pro Collection',
  'DaylightMax Collection',
  'Madeira Collection',
  '6200 Collection',
  'Contractor Collection',
  'Builder Collection',
  'Brickmould 600 Collection',
  'Brickmould 300 Collection',
];


const StepTwo: React.FC<StepTwoProps> = ({ form, setForm, onNext }) => {
  const [availableWindowTypes, setAvailableWindowTypes] = useState<string[]>(brandWindowTypesMap.default);
  const [availableProductSeries, setAvailableProductSeries] = useState<string[]>([]);
  const [availableProductLines, setAvailableProductLines] = useState<string[]>([]);

  useEffect(() => {
    const brand = form.Brand ?? '';
    const key = brandKeyMap[brand] ?? 'default';

    setAvailableWindowTypes(brandWindowTypesMap[key]);
    setAvailableProductSeries(brandProductSeriesMap[key] ?? []);
    setAvailableProductLines(brandProductLinesMap[key] ?? []);
  }, [form.Brand]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleCheckbox = (value: string) => {
    const current = form.WindowTypes ?? [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setForm((prev) => ({ ...prev, WindowTypes: updated }));
  };

  return (
    <Box className='vp-container'>
      <Grid container
        columnSpacing={4}
        rowSpacing={2}
        alignItems="start"
        sx={{
          width: '100%',
          backgroundColor: '#e2e2e2',
          borderRadius: 2,
          px: 3,
          py: 4
        }}>

        <Grid item xs={12} sm={6} container direction="column" rowSpacing={4}>
          <Grid item xs={12} display={'flex'} flexDirection={'column'}>
            <label className="form-label">Brand</label>
            <FormControl component="fieldset" style={{ marginTop: '5px' }}>
              <RadioGroup
                name="Brand"
                value={form.Brand ?? (brands[0] ?? '')}
                onChange={handleInputChange}>
                <Grid container spacing={1}>
                  {(form.IsNavigatedFromProduct
                    ? [form.Brand]
                    : brands
                  ).map((brand) => (
                    <Grid item xs={12} sm={12} key={brand}>
                      <FormControlLabel
                        value={brand}
                        control={<Radio size="small" />}
                        label={<span className="form-label-text">{brand}</span>}
                        disabled={form.IsNavigatedFromProduct && brand !== form.Brand}
                      />
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>
            </FormControl>
          </Grid>

          {form.Brand?.toLowerCase().includes('other') && (
            <Grid item xs={12}>
              <label className="form-label" htmlFor="OtherBrand">Other Brand</label>
              <TextField
                id="OtherBrand"
                name="OtherBrand"
                value={form.OtherBrand ?? ''}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, OtherBrand: e.target.value }))
                }
                fullWidth
                size="small"
                className="form-input"
              />
            </Grid>
          )}

          {!form.Brand?.toLowerCase().includes('mi windows & doors') && (
            <Grid item xs={12}>
              <label className="form-label" htmlFor="Materials">Window Material</label>
              <TextField
                select
                id="Materials"
                name="Materials"
                value={form.Materials?.[0] ?? ''}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, Materials: [e.target.value] }))
                }
                fullWidth
                size="small"
                className="form-input"
              >
                {materials.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {form.Brand?.toLowerCase().includes('simonton') && (
            <Grid item xs={12}>
              <label className="form-label" htmlFor="WindowCollection">Window Collection</label>
              <Autocomplete
                multiple
                size='small'
                className="form-input"
                id="WindowCollection"
                options={simontonWindowCollections}
                value={form.WindowCollection ?? []}
                onChange={(_, value) =>
                  setForm((prev) => ({
                    ...prev,
                    WindowCollection: value,
                  }))
                }
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                  />
                )}
              />
            </Grid>
          )}

          {availableProductSeries.length > 0 && (
            <Grid item xs={12}>
              <label className="form-label" htmlFor="ProductSeries">Product Series</label>
              <TextField
                select
                id="ProductSeries"
                name="ProductSeries"
                value={form.ProductSeries?.[0] ?? ''}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, ProductSeries: [e.target.value] }))
                }
                fullWidth
                size="small"
                className="form-input"
              >
                {availableProductSeries.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {availableProductLines.length > 0 && (
            <Grid item xs={12}>
              <label className="form-label" htmlFor="ProductLines">Product Line</label>
              <Autocomplete
                multiple
                size='small'
                className="form-input"
                id="ProductLines"
                options={availableProductLines}
                value={form.ProductLines ?? []}
                onChange={(_, value) =>
                  setForm((prev) => ({
                    ...prev,
                    ProductLines: value,
                  }))
                }
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} >
            <label className="form-label" htmlFor="NumberOfWindows">Total Number of Windows Needed</label>
            <TextField
              select
              id="NumberOfWindows"
              name="NumberOfWindows"
              value={form.NumberOfWindows ?? ''}
              onChange={handleInputChange}
              fullWidth
              size="small"
              className="form-input"
            >
              {windowCounts.map((item) => (
                <MenuItem key={item} value={item} style={{ minHeight: "36px" }}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

        </Grid>

        <Grid item xs={12} sm={6} container direction="column" rowSpacing={4}>
          <Grid item xs={12}>
            <div className="form-group">
              <label className="form-label">Window Type</label>
              <Grid container spacing={1}>
                {availableWindowTypes.map((type) => (
                  <Grid item xs={6} sm={6} key={type}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={form.WindowTypes?.includes(type) || false}
                          onChange={() => toggleCheckbox(type)}
                        />
                      }
                      label={<span className="form-label-text">{type}</span>}
                    />
                  </Grid>
                ))}
              </Grid>
            </div>
          </Grid>

          <Grid item xs={12}>
            <label className="form-label" htmlFor="Grid">Grids</label>
            <TextField
              select
              id="Grid"
              name="Grid"
              value={form.Grid === undefined ? '' : form.Grid ? 'Yes' : 'No'}
              onChange={(e) => {
                const value = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  Grid:
                    value === 'Yes'
                      ? true
                      : value === 'No'
                        ? false
                        : undefined,
                }));
              }}
              fullWidth
              size="small"
              className="form-input"
            >
              <MenuItem value="" style={{ minHeight: "36px" }}></MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <label className="form-label" htmlFor="Screens">Screens</label>
            <TextField
              select
              id="Screens"
              name="Screens"
              value={form.Screens === undefined ? '' : form.Screens ? 'Yes' : 'No'}
              onChange={(e) => {
                const value = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  Screens:
                    value === 'Yes'
                      ? true
                      : value === 'No'
                        ? false
                        : undefined,
                }));
              }}
              fullWidth
              size="small"
              className="form-input"

            >
              <MenuItem value="" style={{ minHeight: "36px" }}></MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
            {form.Screens === true && (
              <FormControl component="fieldset" sx={{ mt: 3 }}>
                <RadioGroup
                  row
                  name='Screen'
                  value={form.Screen ?? "Full Screen"}
                  onChange={handleInputChange}>
                  <FormControlLabel value="Full Screen" control={<Radio size="small" />}
                    label={<span className="form-label-text">Full Screen</span>} />
                  <FormControlLabel value="Half Screen" control={<Radio size="small" />}
                    label={<span className="form-label-text">Half Screen</span>} />
                </RadioGroup>
              </FormControl>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Button className='blueButtonOutline submit-button-blue' onClick={onNext}>
        Next
      </Button>
    </Box>
  );
};

export default StepTwo;
